/**
 * Search — DESIGN.md §12.4.
 *
 * The full-page counterpart to ⌘K, over the same endpoint and the same
 * operator grammar. The query lives in the URL so a search is shareable and
 * survives a reload.
 */
import { useEffect, useRef, useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { Search as SearchIcon } from 'lucide-react';
import { Page } from '@/components/page';
import { Input } from '@/components/ui/input';
import { EmptyState } from '@/components/ui/empty-state';
import { LoadingRegion } from '@/components/ui/spinner';
import { UserAvatar } from '@/components/ui/avatar';
import { useSearch } from '@/features/search/api';
import { cn, relativeTime } from '@/lib/utils';

const searchSchema = z.object({ q: z.string().optional() });

export const Route = createFileRoute('/search')({
  validateSearch: searchSchema,
  component: SearchPage,
});

const OPERATOR_HELP = [
  ['assignee:me', 'cards assigned to you'],
  ['status:"in testing"', 'by status name'],
  ['tag:regression', 'by tag'],
  ['project:TLC', 'by project key'],
  ['is:blocked', 'blocked cards'],
  ['is:completed', 'include finished work'],
  ['due:today', 'or due:overdue, due:week'],
  ['before:2026-09-01', 'and after:'],
  ['-assignee:tom', 'a leading minus negates'],
] as const;

function SearchPage(): JSX.Element {
  const { q } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const [draft, setDraft] = useState(q ?? '');

  /**
   * The input and the URL have to stay in step in both directions, and the
   * naive pair of effects — "copy q into draft" plus "debounce draft into
   * q" — fights itself: arriving at a *different* `?q=` (a deep link, the
   * back button, ⌘K handing over) leaves the stale draft in state just long
   * enough for the debounce to push it back, silently reverting the
   * navigation.
   *
   * `lastPushed` breaks the loop by recording which value the two are already
   * agreed on. An incoming `q` that differs from it came from outside and is
   * adopted; a `draft` that differs from it came from the keyboard and is
   * pushed.
   */
  const lastPushed = useRef(q ?? '');

  useEffect(() => {
    const incoming = q ?? '';
    if (incoming === lastPushed.current) return;
    lastPushed.current = incoming;
    setDraft(incoming);
  }, [q]);

  useEffect(() => {
    if (draft === lastPushed.current) return;
    // Debounced: typing should not push a history entry per keystroke, but
    // the URL stays the source of truth for the query.
    const handle = setTimeout(() => {
      lastPushed.current = draft;
      void navigate({ search: draft ? { q: draft } : {}, replace: true });
    }, 250);
    return () => clearTimeout(handle);
  }, [draft, navigate]);

  const { data, isFetching } = useSearch(q ?? '', { limit: 25 });
  const hasQuery = (q ?? '').trim() !== '';
  /**
   * The exact-`#number` hit counts. It is returned *outside* `tasks` (§12.4
   * short-circuits to it), so leaving it out of this sum makes searching for a
   * card number that matches nothing else render "Nothing matched" directly
   * above the card it found.
   */
  const total =
    (data?.exactCard ? 1 : 0) +
    (data?.tasks.length ?? 0) +
    (data?.projects.length ?? 0) +
    (data?.comments.length ?? 0);

  return (
    <Page title="Search" description="Cards, projects and comments — with operators.">
      <div className="mb-4 space-y-2">
        <Input
          autoFocus
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder="login bug assignee:tom is:blocked"
          aria-label="Search query"
        />
        <div className="flex flex-wrap gap-1.5">
          {OPERATOR_HELP.map(([operator, hint]) => (
            <button
              key={operator}
              type="button"
              title={hint}
              onClick={() => setDraft((value) => `${value} ${operator}`.trim())}
              className="bg-muted hover:bg-accent text-muted-foreground rounded px-1.5 py-0.5 font-mono text-[0.7rem]"
            >
              {operator}
            </button>
          ))}
        </div>
        {data && data.interpretation.terms.length > 0 ? (
          <p className="flex flex-wrap items-center gap-1 text-xs">
            <span className="text-muted-foreground">Understood:</span>
            {data.interpretation.terms.map((term, index) => (
              <span
                key={`${term.operator}-${index}`}
                className={cn(
                  'rounded px-1.5 py-0.5 font-mono text-[0.7rem]',
                  term.resolved
                    ? 'bg-primary/10 text-primary'
                    : 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
                )}
                title={term.resolved ? 'Applied' : 'Nothing matched that name — ignored'}
              >
                {term.negated ? '-' : ''}
                {term.operator}:{term.value}
              </span>
            ))}
            {data.interpretation.text ? (
              <span className="text-muted-foreground">+ text “{data.interpretation.text}”</span>
            ) : null}
          </p>
        ) : null}
      </div>

      {!hasQuery ? (
        <EmptyState
          icon={<SearchIcon className="size-8" />}
          title="Search everything"
          description="Card titles and descriptions, project names, and comment bodies. Type a card number — 1042 or #1042 — to jump straight to it, or use an operator to narrow things down."
        />
      ) : isFetching && !data ? (
        <LoadingRegion label="Searching" />
      ) : total === 0 ? (
        <EmptyState
          icon={<SearchIcon className="size-8" />}
          title="Nothing matched"
          description="Try fewer words. Completed cards are included in search, so this is not a “Show completed” problem."
        />
      ) : (
        <div className="space-y-6">
          {data?.exactCard ? (
            <section>
              <h2 className="text-muted-foreground mb-1.5 text-xs font-semibold uppercase tracking-wide">
                Exact match
              </h2>
              <CardHit card={data.exactCard} />
            </section>
          ) : null}

          {(data?.tasks.length ?? 0) > 0 ? (
            <section>
              <h2 className="text-muted-foreground mb-1.5 text-xs font-semibold uppercase tracking-wide">
                Cards · {data!.tasks.length}
              </h2>
              <ul className="divide-y rounded-lg border">
                {data!.tasks.map((card) => (
                  <li key={card.id}>
                    <CardHit card={card} bare />
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {(data?.projects.length ?? 0) > 0 ? (
            <section>
              <h2 className="text-muted-foreground mb-1.5 text-xs font-semibold uppercase tracking-wide">
                Projects · {data!.projects.length}
              </h2>
              <ul className="divide-y rounded-lg border">
                {data!.projects.map((project) => (
                  <li key={project.id}>
                    <Link
                      to="/projects/$key"
                      params={{ key: project.key }}
                      className="hover:bg-accent/50 flex items-center gap-2 px-3 py-2 text-sm"
                    >
                      <span
                        className="size-2.5 shrink-0 rounded"
                        style={{ backgroundColor: project.color }}
                        aria-hidden="true"
                      />
                      <span className="flex-1 truncate">{project.name}</span>
                      <span className="text-muted-foreground font-mono text-xs">{project.key}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {(data?.comments.length ?? 0) > 0 ? (
            <section>
              <h2 className="text-muted-foreground mb-1.5 text-xs font-semibold uppercase tracking-wide">
                Comments · {data!.comments.length}
              </h2>
              <ul className="divide-y rounded-lg border">
                {data!.comments.map((hit) => (
                  <li key={hit.id}>
                    <Link
                      to="/card/$number"
                      params={{ number: String(hit.task.number) }}
                      className="hover:bg-accent/50 flex items-start gap-2 px-3 py-2 text-sm"
                    >
                      <UserAvatar user={hit.author} className="mt-0.5 size-5 shrink-0" />
                      <span className="min-w-0 flex-1">
                        <span className="text-muted-foreground text-xs">
                          {hit.author.name} on #{hit.task.number} · {relativeTime(hit.createdAt)}
                        </span>
                        <span className="block truncate">{hit.snippet}</span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
        </div>
      )}
    </Page>
  );
}

function CardHit({
  card,
  bare = false,
}: {
  card: {
    id: string;
    number: number;
    title: string;
    statusName: string;
    statusColor: string;
    completedAt: string | null;
  };
  bare?: boolean;
}): JSX.Element {
  return (
    <Link
      to="/card/$number"
      params={{ number: String(card.number) }}
      className={cn(
        'hover:bg-accent/50 flex items-center gap-2 px-3 py-2 text-sm',
        !bare && 'rounded-lg border',
      )}
    >
      <span className="text-muted-foreground w-12 shrink-0 font-mono text-xs">#{card.number}</span>
      <span
        className={cn(
          'min-w-0 flex-1 truncate',
          card.completedAt && 'text-muted-foreground line-through',
        )}
      >
        {card.title}
      </span>
      <span
        className="shrink-0 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium"
        style={{ backgroundColor: `${card.statusColor}1f`, color: card.statusColor }}
      >
        {card.statusName}
      </span>
    </Link>
  );
}
